import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaRocket, FaCode, FaServer, FaMobileAlt, FaRobot,
  FaMicrosoft, FaChevronRight, FaArrowRight, FaPlay
} from 'react-icons/fa';
import './HomePage.css';

/* ── Animated Counter Hook ── */
function useCountUp(target: number, suffix: string = '', duration: number = 2000) {
  const [count, setCount] = useState('0');
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const step = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const ease = 1 - Math.pow(1 - progress, 3);
            const value = Math.round(ease * target);
            setCount(value + suffix);
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, suffix, duration]);

  return { ref, count };
}

/* ── Scroll Reveal Hook ── */
function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  // Parallax
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);



  const s1 = useScrollReveal();
  const s2 = useScrollReveal();
  const s3 = useScrollReveal();
  const s4 = useScrollReveal();

  const services = [
    { icon: <FaMicrosoft />, title: 'Microsoft 365 & Power Platform', desc: 'SharePoint, Power Apps, Power Automate & Copilot solutions.', img: '/images/tech_dashboard.png' },
    { icon: <FaCode />, title: 'Web Development & Modern UI', desc: 'React.js & .NET applications with cutting-edge design.', img: '/images/services_consultant.png' },
    { icon: <FaServer />, title: 'Cloud & Digital Modernisation', desc: 'Azure migrations, microservices & DevOps implementation.', img: '/images/tech_bg.png' },
    { icon: <FaMobileAlt />, title: 'Mobile App Development', desc: 'Cross-platform Flutter apps with native performance.', img: '/images/services_workshop.png' },
    { icon: <FaRobot />, title: 'AI & Process Automation', desc: 'Intelligent workflows, RPA & AI-driven solutions.', img: '/images/tech_dashboard.png' },
    { icon: <FaCode />, title: 'API Development & Integration', desc: 'Secure, scalable RESTful APIs in Node.js & .NET.', img: '/images/api_integration.png' },
  ];

  const pillars = [
    { num: '01', title: 'Proven Expertise', desc: 'Deep Microsoft technology mastery with senior-level enterprise consultants.' },
    { num: '02', title: 'Innovation-Driven', desc: 'We use the latest AI, cloud, and automation tools to future-proof your systems.' },
    { num: '03', title: 'Agile Delivery', desc: 'Rapid, iterative delivery with transparent milestones and quality assurance.' },
    { num: '04', title: '24/7 Support', desc: 'Round-the-clock monitoring and dedicated account management.' },
  ];

  return (
    <main className="home-page">
      {/* ── Hero ── */}
      <section className="hero-section">
        {/* Left half: text */}
        <div className="hero-left">
          <div className="hero-bg-glow" />
          <div className="hero-stars" />
          <div className="hero-content">
            <div className="hero-badges">
              <span className="hero-badge"><span className="hero-badge-dot" />AI-Powered</span>
              <span className="hero-badge"><span className="hero-badge-dot" />Enterprise-Grade</span>
            </div>
            <h1 className="hero-title fade-in-up">
              Intelligent IT.<br />
              <span className="gradient-text">Digital Future.</span><br />
              Built for You.
            </h1>
            <p className="hero-subtitle fade-in-up delay-1">
              CoreNuVate IT combines cutting-edge technology with deep enterprise expertise
              to deliver cloud, automation, and software solutions that drive real business growth.
            </p>
            <div className="hero-buttons fade-in-up delay-2">
              <button className="btn-primary" onClick={() => navigate('/contact')}>
                <FaRocket /> Get Started Today
              </button>
              <button className="btn-secondary" onClick={() => navigate('/services')}>
                Explore Services <FaChevronRight />
              </button>
            </div>


          </div>
        </div>

        {/* Right half: human image with parallax */}
        <div className="hero-right">
          <div className="hero-img-wrapper" style={{ transform: `translateY(${scrollY * 0.12}px)` }}>
            <img src="/images/hero_team.png" alt="CoreNuVate IT team" className="hero-img" />
            {/* Floating tech overlay badge */}

            <div className="hero-float-card card-bottom-left">
              <div className="float-icon"><FaRobot /></div>
              <div>
                <div className="float-label">AI Automation</div>
                <div className="float-value">Deployed ✓</div>
              </div>
            </div>
            {/* Dashboard overlay on image */}
            <div className="hero-dashboard-overlay">
              <img src="/images/tech_dashboard.png" alt="Tech dashboard" className="hero-dashboard-img" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Services Preview ── */}
      <section
        className={`section home-services reveal-section ${s1.visible ? 'revealed' : ''}`}
        ref={s1.ref}
      >
        <div className="container">
          <div className="section-header text-center mb-64">
            <span className="section-label">What We Do</span>
            <h2 className="section-title">Enterprise IT Services<br /><span>Built to Scale</span></h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              From cloud infrastructure to AI automation — we deliver end-to-end technology solutions tailored to your business.
            </p>
          </div>
          <div className="services-preview-grid">
            {services.map((s, i) => (
              <div
                key={i}
                className="glass-card service-preview-card"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                {/* Image thumbnail with hover reveal */}
                <div className="svc-img-wrap">
                  <img src={s.img} alt={s.title} className="svc-img" />
                  <div className="svc-img-overlay" />
                </div>
                <div className="svc-body">
                  <div className="icon-box">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <button className="card-link" onClick={() => navigate('/services')}>
                    Learn more <FaArrowRight />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center" style={{ marginTop: '48px' }}>
            <button className="btn-secondary" onClick={() => navigate('/services')}>
              View All Services <FaChevronRight />
            </button>
          </div>
        </div>
      </section>

      {/* ── Why CoreNuVate — Split image layout ── */}
      <section
        className={`section home-why reveal-section ${s2.visible ? 'revealed' : ''}`}
        ref={s2.ref}
      >
        <div className="container">
          <div className="why-layout">
            <div className="why-left">
              <span className="section-label">Why Us</span>
              <h2 className="section-title">Why Businesses<br /><span>Choose CoreNuVate</span></h2>
              <p className="section-subtitle">
                We don't just deliver technology — we become your long-term digital transformation partner.
              </p>
              <button className="btn-primary" style={{ marginTop: '32px' }} onClick={() => navigate('/why-us')}>
                Discover Why <FaArrowRight />
              </button>

              {/* Human image with floating overlay */}
              <div className="why-img-block">
                <img src="/images/why_choose_us.png" alt="Client partnership" className="why-img" />
                <div className="why-img-badge">
                  <FaPlay className="badge-icon" />
                  <div>
                    <div className="float-label">Trusted by</div>
                    <div className="float-value">Growing Client Base</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="why-right">
              {pillars.map((p, i) => (
                <div key={i} className="pillar-card glass-card" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="pillar-num">{p.num}</div>
                  <div className="pillar-body">
                    <h4>{p.title}</h4>
                    <p>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Team Showcase ── */}
      <section
        className={`section home-team reveal-section ${s3.visible ? 'revealed' : ''}`}
        ref={s3.ref}
      >
        <div className="container">
          <div className="team-showcase-layout">
            <div className="team-img-stack">
              <img src="/images/about_team.png" alt="CoreNuVate team collaboration" className="team-main-img" />
              <div className="team-img-overlay">
                <img src="/images/tech_bg.png" alt="tech background" className="team-tech-overlay-img" />
              </div>
              <div className="team-stat-badge">
                <span className="t-stat-num">100%</span>
                <span className="t-stat-label">Commitment</span>
              </div>
            </div>
            <div className="team-text">
              <span className="section-label">Our Team</span>
              <h2 className="section-title">Real People.<br /><span>Real Expertise.</span></h2>
              <p className="section-subtitle">
                Behind every great project is a team that genuinely cares about your outcomes.
                Our consultants, architects, and developers bring deep domain knowledge and a
                partnership mindset to every engagement.
              </p>
              <div className="team-highlights">
                {[
                  'Microsoft-certified engineers and architects',
                  'Multilingual team, Netherlands-based',
                  'Agile scrum masters embedded in every project',
                  'Dedicated account manager for every client',
                ].map((h, i) => (
                  <div key={i} className="team-highlight-item">
                    <span className="highlight-dot" />
                    <span>{h}</span>
                  </div>
                ))}
              </div>
              <button className="btn-primary" style={{ marginTop: '32px' }} onClick={() => navigate('/about')}>
                Meet the Team <FaArrowRight />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Tech Strip ── */}
      <section className="home-tech-strip">
        <div className="container">
          <p className="strip-label">Trusted technologies we work with</p>
          <div className="tech-tags">
            {['React.js', 'Microsoft Azure', 'SharePoint', '.NET', 'Power Platform', 'Flutter',
              'Node.js', 'SQL Server', 'Power BI', 'Dataverse', 'Microsoft 365', 'Docker'].map(t => (
                <span key={t} className="tech-tag">{t}</span>
              ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section
        className={`section reveal-section ${s4.visible ? 'revealed' : ''}`}
        ref={s4.ref}
      >
        <div className="container">
          <div className="cta-banner cta-with-img">
            <div className="cta-content">
              <span className="section-label" style={{ justifyContent: 'center' }}>Let's Talk</span>
              <h2>Ready to Transform<br /><span className="gradient-text">Your Business?</span></h2>
              <p>Schedule a free consultation and let's explore how CoreNuVate IT can accelerate your digital journey.</p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="btn-primary" onClick={() => navigate('/contact')}>
                  <FaRocket /> Start Your Project
                </button>
                <button className="btn-secondary" onClick={() => navigate('/engagement')}>
                  View Engagement Models
                </button>
              </div>
            </div>
            <div className="cta-img-wrap">
              <img src="/images/contact_team.png" alt="Support team" className="cta-img" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
